import { useState } from 'react';
import { Link } from 'react-router-dom';
import { categories } from '@/mocks/categories';

const footerLinks = [
  {
    title: 'Guide',
    links: [
      { label: 'Guida Completa', to: '/articoli/itaobuy-spreadsheet-guida-completa' },
      { label: 'Per Principianti', to: '/articoli/itaobuy-spreadsheet-per-principianti' },
      { label: 'Strategie Avanzate', to: '/articoli/itaobuy-spreadsheet-strategie-avanzate' },
      { label: 'Come Usare', to: '/articoli/come-usare-itaobuy-spreadsheet' },
    ],
  },
  {
    title: 'Categorie',
    links: categories.slice(0, 7).map((c) => ({
      label: c.name,
      href: c.url,
      external: true,
    })),
  },
  {
    title: 'Risorse',
    links: [
      { label: 'Sicurezza', to: '/articoli/itaobuy-spreadsheet-sicurezza' },
      { label: 'Confronto', to: '/articoli/itaobuy-spreadsheet-vs-alternative' },
      { label: 'Classifica 2026', to: '/articoli/migliore-itaobuy-spreadsheet' },
      { label: 'Recensioni', to: '/articoli/itaobuy-spreadsheet-recensioni' },
      { label: 'Categorie', to: '/articoli/itaobuy-spreadsheet-categorie' },
    ],
  },
];

export default function Footer() {
  const [newsletterStatus, setNewsletterStatus] = useState<null | 'success' | 'error'>(null);
  const [newsletterMsg, setNewsletterMsg] = useState('');

  const handleNewsletterSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    const honeypotVal = (formData.get('phone_alt') as string || '').trim();
    if (honeypotVal) {
      setNewsletterStatus('success');
      setNewsletterMsg('Grazie per l\'iscrizione!');
      form.reset();
      return;
    }
    try {
      const res = await fetch('https://readdy.ai/api/form/d9b43scngs93pt5sh7m0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await res.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string }; message?: string } = {};
      try { parsed = JSON.parse(responseText); } catch { /* ignore parse error */ }
      const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || '';
      if (res.ok && parsed?.code === 'OK') {
        setNewsletterStatus('success');
        setNewsletterMsg('Grazie per l\'iscrizione!');
        form.reset();
      } else {
        setNewsletterStatus('error');
        setNewsletterMsg(serverMsg || 'Si è verificato un errore. Riprova.');
      }
    } catch {
      setNewsletterStatus('error');
      setNewsletterMsg('Errore di rete. Riprova più tardi.');
    }
  };

  return (
    <footer className="bg-primary-900 text-white">
      <div className="max-w-[1200px] mx-auto px-6 md:px-10 py-16 md:py-20">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-12 mb-12">
          <div>
            <div className="mb-6">
              <p className="font-heading text-xl font-semibold tracking-wide">ITAOBUY SPREAD.</p>
            </div>

            <form
              onSubmit={handleNewsletterSubmit}
              data-readdy-form=""
              className="flex flex-col gap-2 mb-6 max-w-[380px]"
            >
              <input
                type="text"
                name="phone_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
                className="form-honeypot"
              />
              <div className="flex gap-2">
                <input
                  type="email"
                  name="email"
                  placeholder="La tua email"
                  required
                  className="flex-1 bg-white/10 border border-white/20 rounded-full px-5 py-3 text-sm text-white placeholder-white/40 outline-none focus:border-white/40 transition-colors"
                />
                <button
                  type="submit"
                  className="w-11 h-11 rounded-full bg-white/15 border border-white/20 flex items-center justify-center hover:bg-white/25 transition-colors cursor-pointer flex-shrink-0"
                >
                  <i className="ri-mail-send-line text-white"></i>
                </button>
              </div>
              {newsletterStatus && (
                <p className={`text-xs ${newsletterStatus === 'success' ? 'text-green-300' : 'text-red-300'}`}>
                  {newsletterMsg}
                </p>
              )}
            </form>

            <p className="font-heading text-2xl md:text-3xl text-white/90 leading-snug">
              La più grande piattaforma
              <br />
              italiana di conoscenza
            </p>
          </div>

          <div className="grid grid-cols-3 gap-8">
            {footerLinks.map((col) => (
              <div key={col.title}>
                <h5 className="text-sm font-semibold text-white mb-4">{col.title}</h5>
                <ul className="space-y-2.5">
                  {col.links.map((link) =>
                    'external' in link && link.external ? (
                      <li key={link.label}>
                        <a
                          href={link.href}
                          target="_blank"
                          rel="noopener nofollow"
                          className="text-sm text-white/60 hover:text-white transition-colors"
                        >
                          {link.label}
                        </a>
                      </li>
                    ) : (
                      <li key={link.label}>
                        <Link
                          to={link.to}
                          className="text-sm text-white/60 hover:text-white transition-colors"
                          onClick={() => window.scrollTo({ top: 0, behavior: 'auto' })}
                        >
                          {link.label}
                        </Link>
                      </li>
                    )
                  )}
                </ul>
              </div>
            ))}
          </div>
        </div>

        <div className="border-t border-white/15 pt-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-sm text-white/50">
            &copy; 2026 Itaobuy Spread. Tutti i diritti riservati.
          </p>
          <div className="flex items-center gap-4">
            {['ri-twitter-x-line', 'ri-instagram-line', 'ri-pinterest-line', 'ri-youtube-line'].map((icon) => (
              <a
                key={icon}
                href="#"
                target="_blank"
                rel="noopener nofollow"
                className="w-9 h-9 rounded-lg border border-white/20 flex items-center justify-center text-white/60 hover:text-white hover:border-white/40 transition-colors"
              >
                <i className={icon}></i>
              </a>
            ))}
          </div>
          <a
            href="https://kaspreadsheet.com"
            target="_blank"
            rel="noopener nofollow"
            className="text-sm text-white/50 hover:text-white/80 transition-colors"
          >
            Privacy Policy
          </a>
        </div>
      </div>
    </footer>
  );
}